/**
 * Etwas in die Zwischenablage legen -- und ehrlich sagen, ob es geklappt hat.
 *
 * Drei Dinge gehen dabei schief, und alle drei sind auf dem iPhone passiert.
 *
 * 1. `navigator.clipboard` gibt es nur auf einer sicheren Verbindung. Ohne
 *    HTTPS ist das Feld schlicht nicht da.
 * 2. Safari nimmt einen Schreibversuch nur an, solange die Beruehrung noch
 *    laeuft. Steht ein `await` dazwischen -- eine Anfrage an den Server etwa
 *    --, ist die Geste vorbei und der Versuch wird abgelehnt.
 * 3. Und dann ist da noch der aeltere Weg ueber ein verstecktes Feld. Der
 *    funktioniert dort, wo der neue nicht darf.
 *
 * Deshalb: erst der neue Weg, dann der alte, und am Ende ein `false`, mit dem
 * der Aufrufer etwas anderes anbieten kann. Ein „Kopiert“, das nicht stimmt,
 * ist schlimmer als keins -- dann sucht man den Text in der Zwischenablage,
 * wo er nie war.
 */
export async function kopiere(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* Weiter zum alten Weg. */
  }
  try {
    const feld = document.createElement('textarea');
    feld.value = text;
    // Nicht `display: none` und nicht `hidden`: ein Feld, das nicht im
    // Layout steht, laesst sich auch nicht auswaehlen, und ohne Auswahl
    // kopiert der alte Weg nichts.
    feld.setAttribute('readonly', '');
    feld.style.position = 'fixed';
    feld.style.top = '0';
    feld.style.opacity = '0';
    feld.style.pointerEvents = 'none';
    document.body.appendChild(feld);
    feld.focus();
    feld.select();
    // iOS hoert nicht auf `select()` allein.
    feld.setSelectionRange(0, text.length);
    const ok = document.execCommand('copy');
    feld.remove();
    return ok;
  } catch {
    return false;
  }
}

/**
 * **Kopieren, was erst noch vom Server kommt** -- in derselben Berührung
 * (Sascha, 4. Oktober: „Wenn ich einen Link zum Mitschneiden mache, kopiert es
 * ihn nicht in die Zwischenablage obwohl es dran steht“).
 *
 * Das ist Punkt 2 von oben: der neue Link entsteht auf dem Server, und bis er
 * da ist, ist die Geste vorbei -- Safari lehnt `writeText` danach ab. Der Weg
 * darum herum ist ein `ClipboardItem`, dem man ein *Versprechen* gibt: der
 * Schreibversuch beginnt noch in der Berührung, der Inhalt kommt nach. Diese
 * Funktion muss deshalb **vor** dem ersten `await` aufgerufen werden.
 *
 * Kennt der Browser das nicht oder lehnt es ab, versucht sie es danach noch
 * einmal auf dem gewöhnlichen Weg und sagt ehrlich, ob es geklappt hat.
 */
export function kopiereGleich(text: Promise<string>): Promise<boolean> {
  const Eintrag = (globalThis as { ClipboardItem?: typeof ClipboardItem }).ClipboardItem;
  let versuch: Promise<boolean> = Promise.resolve(false);
  if (Eintrag && navigator.clipboard?.write) {
    try {
      versuch = navigator.clipboard
        .write([new Eintrag({ 'text/plain': text.then((t) => new Blob([t], { type: 'text/plain' })) })])
        .then(() => true, () => false);
    } catch {
      versuch = Promise.resolve(false);
    }
  }
  return versuch.then(async (ok) => {
    if (ok) return true;
    try {
      return await kopiere(await text);
    } catch {
      return false;
    }
  });
}
